import { ChangeEvent, useState } from 'react'
import { Callback, ReceivingFileModel } from 'Pages/landings/newLandings/common/types'

interface elInfo {
	title: string
	info: string
	isAnimation?: boolean
	timeout?: any
}

export const useForm = <T extends Record<keyof T, any> = {}>() => {
	const [formValue, setFormValue] = useState<T>({} as T)
	const [formErrors, setFormErrors] = useState<
		Record<string, { errText: string; validation?: Callback<unknown, string>[] }>
	>({})
	const [formFileErrors, setFormFileErrors] = useState<Record<string, elInfo[] | string[]>>({})
	const getError = (fieldName: string, value: unknown) =>
		formErrors[fieldName]?.validation?.map((validator) => validator(value)).filter((err) => err) ??
		''
	const validateFields = () => {
		const isErr = Object.keys(formErrors).filter((field) => {
			const errText = getError(field, formValue[field])[0]
			if (errText) {
				formErrors[field] = { ...formErrors[field], errText }
				return true
			}
			return false
		}).length
		setFormErrors((prevFormErrors) => ({ ...prevFormErrors }))
		return Boolean(isErr)
	}
	const validateFileFields = () =>
		Object.keys(formFileErrors).filter((field) => {
			const errText = getError(field, formValue[field])
			if (errText?.length) {
				setFormFileErrors((prevFormValue) => ({
					...prevFormValue,
					[field]: [...errText],
				}))
				return true
			}
			return false
		}).length

	const validateField = (fieldName: string, value: string | ReceivingFileModel[]) => {
		if (!formErrors[fieldName]?.validation.length) return
		setFormErrors((prevFormErrors) => ({
			...prevFormErrors,
			[fieldName]: { ...prevFormErrors[fieldName], errText: '' },
		}))
		const errText = getError(fieldName, value)[0]
		if (errText) {
			setFormErrors((prevFormErrors) => ({
				...prevFormErrors,
				[fieldName]: { ...prevFormErrors[fieldName], errText },
			}))
		}
	}

	const createChangeHandler = ({
		fieldName,
		validation,
		initialValue,
		normalize,
	}: {
		fieldName: string
		validation?: Callback<unknown, string>[]
		initialValue?: string | boolean | Date
		normalize?: Callback<string, string>
	}) => {
		if (!(fieldName in formValue))
			setFormValue((prevFormValue) => ({
				...prevFormValue,
				[fieldName]: !fieldName.includes('file') ? initialValue || '' : [],
			}))
		if (
			(validation && validation?.length !== formErrors[fieldName]?.validation.length) ||
			!(fieldName in formErrors)
		)
			setFormErrors((prevFormErrors) => ({
				...prevFormErrors,
				[fieldName]: { errText: '', validation },
			}))
		if (!formFileErrors?.[fieldName])
			setFormFileErrors((prevFormValue) => ({
				...prevFormValue,
				[fieldName]: [],
			}))
		return (event: ChangeEvent<HTMLInputElement> | string) => {
			const e = event as ChangeEvent<HTMLInputElement>
			const isEvent = Object.hasOwnProperty.call(e, 'currentTarget')
			if (isEvent && e.currentTarget.type === 'checkbox') {
				setFormValue((prevFormValue) => ({
					...prevFormValue,
					[fieldName]: !prevFormValue[fieldName],
				}))
				return
			}
			if (isEvent && e.currentTarget.type === 'file') {
				const acceptFiles: ReceivingFileModel[] = Array.from(e.currentTarget.files ?? [])
				const validateFieldss = getError(fieldName, [
					...formValue[fieldName],
					...e.currentTarget.files,
				])
				if (validateFieldss.length) {
					setFormFileErrors((prevFormValue) => ({
						...prevFormValue,
						[fieldName]: [...validateFieldss],
					}))
				} else {
					setFormValue((prevFormValue) => ({
						...prevFormValue,
						[fieldName]: [...prevFormValue[fieldName], ...acceptFiles],
					}))
				}
				return
			}
			const value = isEvent ? e.currentTarget.value : (event as string)
			const normalizedVal = normalize ? normalize(value) : value
			setFormValue((prevFormValue) => ({
				...prevFormValue,
				[fieldName]: normalizedVal,
			}))
			validateField(fieldName, normalizedVal)
		}
	}

	const getFormValue = (key: string) => String(formValue?.[key] ?? '')
	const getFormError = (key: string) => formErrors?.[key]?.errText ?? ''
	const checked = (key: string) => Boolean(formValue?.[key])
	const getFormDate = (key: string) => (formValue?.[key] ? new Date(formValue?.[key]) : '')
	const clearForm = () => {
		if (Object.values(formValue).length) setFormValue({} as T)
		if (Object.values(formErrors).length) setFormErrors({})
		if (Object.values(formFileErrors).length) setFormFileErrors({})
	}
	const submit = (onSubmit?: Callback) => {
		validateFileFields?.()
		if (!validateFields() && onSubmit) {
			return onSubmit()
		}
	}

	return {
		formValue,
		formFileErrors,
		setFormValue,
		createChangeHandler,
		submit,
		getFormError,
		getFormValue,
		checked,
		getFormDate,
		validateFields,
		clearForm,
		formErrors,
		validateField,
		setFormErrors,
	}
}
